import { useEffect, useState } from 'react';
import type { BuildInfo } from '@linkode/shared';
import { useTypedTranslation } from '../i18n';

/**
 * Footer component that displays version information
 * Fetches build metadata from /version API endpoint
 */
export function Footer() {
  const { t } = useTypedTranslation();
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        // Use VITE_API_URL for API calls, fallback to localhost in dev
        const baseUrl =
          import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8787' : '');

        const response = await fetch(`${baseUrl}/version`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data: BuildInfo = await response.json();
        setBuildInfo(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('footer.fetchFailed'));
        // Fallback to development defaults
        setBuildInfo({
          env: 'development',
          commitSha: 'unknown',
          buildTime: new Date().toISOString(),
        });
      }
    };

    fetchVersion();
  }, []);

  const formatBuildTime = (isoString: string): string => {
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  const SEPARATOR = ' | ';

  return (
    <footer style={footerStyle}>
      <div style={containerStyle}>
        {buildInfo ? (
          <span style={versionStyle}>
            {buildInfo.env}
            {SEPARATOR}
            {buildInfo.commitSha}
            {SEPARATOR}
            {formatBuildTime(buildInfo.buildTime)}
          </span>
        ) : error ? (
          <span style={errorStyle}>{t('footer.versionUnavailable')}</span>
        ) : (
          <span style={loadingStyle}>{t('footer.loadingVersion')}</span>
        )}
      </div>
    </footer>
  );
}

const footerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#f5f5f5',
  borderTop: '1px solid #e0e0e0',
  padding: '8px 16px',
  fontSize: '12px',
  color: '#666',
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const versionStyle: React.CSSProperties = {
  fontFamily: 'monospace',
};

const errorStyle: React.CSSProperties = {
  color: '#999',
};

const loadingStyle: React.CSSProperties = {
  color: '#999',
  fontStyle: 'italic',
};
